"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, ShieldCheck, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
interface BrandItem {
  name: string;
  category: string;
  tagline: string;
  popularProduct: string;
  certified: boolean;
}
const BRANDS_LIST: BrandItem[] = [{
  name: "Rheem",
  category: "Water Heaters & HVAC",
  tagline: "High-efficiency tank & tankless water systems",
  popularProduct: "Performance Platinum Series",
  certified: true
}, {
  name: "Bradford White",
  category: "Commercial & Residential",
  tagline: "Professional-grade American water heaters",
  popularProduct: "AeroTherm Heat Pump & Atmospheric",
  certified: true
}, {
  name: "AO Smith",
  category: "Water Systems",
  tagline: "Industry standard for durable water heaters",
  popularProduct: "Signature & ProLine Series",
  certified: true
}, {
  name: "Navien",
  category: "Tankless Technology",
  tagline: "Ultra-high efficiency condensing tankless units",
  popularProduct: "NPE-A2 Premium Condensing",
  certified: true
}, {
  name: "Rinnai",
  category: "Tankless Water Heaters",
  tagline: "On-demand continuous hot water solutions",
  popularProduct: "Sensei RX & Super-High-Efficiency",
  certified: true
}, {
  name: "Moen",
  category: "Fixtures & Valves",
  tagline: "Reliable faucets, showers, and smart water valves",
  popularProduct: "Flo Smart Water Monitor & Faucets",
  certified: true
}, {
  name: "Delta",
  category: "Fixtures & Commercial",
  tagline: "Touchless technology & leak-free valve cartridges",
  popularProduct: "Touch2O Kitchen & Multichoice Valves",
  certified: true
}, {
  name: "Kohler",
  category: "Premium Fixtures & Sinks",
  tagline: "Luxury and commercial-grade plumbing fittings",
  popularProduct: "Class Five Toilets & Rite-Temp Valves",
  certified: true
}];
export default function Brands() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="servicedbrands" data-nav="light" className="relative bg-background py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Section */}
        <div className="mx-auto max-w-3xl text-center mb-12 md:mb-16">
          <div className="inline-flex items-center justify-center mb-4">
            <Badge variant="secondary" className="px-3.5 py-1 text-xs font-semibold tracking-wide uppercase bg-secondary text-secondary-foreground border border-border">
              <ShieldCheck className="w-3.5 h-3.5 mr-1.5 text-accent" />
              Trusted Brand Expertise
            </Badge>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight mb-4">
            Plumbing Brands We Service
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Our certified Jonesboro technicians carry genuine OEM replacement parts
            and complete factory-approved repairs, diagnostics, and replacements
            for all premier plumbing manufacturers.
          </p>
        </div>

        {/* Brands Grid (2 cols mobile, 4 cols desktop) */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {BRANDS_LIST.map((brand, i) => <motion.div key={brand.name} data-index={i} variants={cardVariants} whileHover={shouldReduceMotion ? {} : {
          y: -4
        }} transition={{
          duration: 0.2
        }} className="h-full">
              <Card className="h-full bg-card border-border shadow-xs hover:shadow-md transition-shadow duration-200 flex flex-col justify-between p-6">
                <CardContent className="p-0 flex flex-col h-full justify-between gap-5">
                  <div>
                    {/* Top row: Brand Name & Certification */}
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <h3 className="text-xl font-bold font-default text-foreground tracking-tight">
                        {brand.name}
                      </h3>
                      {brand.certified && <span className="inline-flex items-center text-xs font-medium text-chart-4 bg-muted px-2 py-0.5 rounded-md border border-border shrink-0">
                          <CheckCircle2 className="w-3 h-3 mr-1 text-chart-4" />
                          Certified
                        </span>}
                    </div>

                    {/* Category */}
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                      {brand.category}
                    </p>

                    {/* Brand description / tagline */}
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {brand.tagline}
                    </p>
                  </div>

                  {/* Highlight feature / Popular products */}
                  <div className="pt-4 border-t border-border flex items-start gap-2 bg-muted/40 -mx-6 -mb-6 p-4 rounded-b-xl">
                    <Wrench className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <div className="text-xs">
                      <span className="text-muted-foreground block font-medium">Commonly Repaired:</span>
                      <span className="text-foreground font-semibold line-clamp-1">
                        {brand.popularProduct}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Action Container */}
        <div className="mt-12 sm:mt-16 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-secondary/50 border border-border p-4 sm:p-5 rounded-2xl max-w-2xl mx-auto shadow-2xs">
            <p className="text-sm text-muted-foreground text-center sm:text-left">
              Have an unlisted make or legacy equipment? We service nearly every plumbing system in Georgia.
            </p>
            <Button asChild variant="default" size="lg" className="font-semibold shrink-0 group shadow-sm">
              <Link to="/brands/">
                View All Brands
                <ArrowRight className="w-4 h-4 ml-1.5 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}